import type { FunctionComponent } from 'react';
import type { z } from 'zod';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { RiskIndicatorSchema } from '@ballerine/common';

import { ctw } from '@/common';
import { Card, CardContent, WarningFilledSvg } from '@/components';
import { markdownComponents } from '@/components/templates/report-shared/components/markdown-components';

export const ContentFindings: FunctionComponent<{
  riskIndicators: Array<z.infer<typeof RiskIndicatorSchema>>;
}> = ({ riskIndicators }) => {
  if (!riskIndicators.length) {
    return (
      <div className="bg-card text-card-foreground rounded-lg border px-6 py-4 shadow-sm">
        No content findings detected for the website.
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4">
      {riskIndicators
        .filter(i => i.riskLevel !== 'positive')
        .map(riskIndicator => {
          const screenshotUrl = riskIndicator.screenshot?.screenshotUrl ?? null;

          return (
            <Card key={riskIndicator.name} data-testid="content-finding">
              <CardContent className={'py-6'}>
                <div className={'mb-2 flex items-center gap-2 text-lg font-semibold'}>
                  <WarningFilledSvg className="h-5 w-5" />
                  <h4>{riskIndicator.name}</h4>
                </div>

                <div className={ctw('grid grid-cols-1 gap-6', screenshotUrl && 'grid-cols-2')}>
                  <div className={ctw('flex-1 space-y-4', screenshotUrl && 'md:pr-6')}>
                    <div>
                      <p className={'font-medium'}>Description</p>
                      <p className={'text-sm leading-snug'}>{riskIndicator.explanation}</p>
                    </div>
                    {riskIndicator.reason && (
                      <div>
                        <p className={'font-medium'}>Why Our AI Flagged This?</p>
                        <p className={'text-sm leading-snug'}>{riskIndicator.reason}</p>
                      </div>
                    )}
                    {riskIndicator.quoteFromSource && (
                      <div>
                        <p className={'font-medium'}>Source</p>
                        <div className={'text-sm italic leading-snug'}>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={markdownComponents}
                          >
                            {`"${riskIndicator.quoteFromSource}"`}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>

                  {screenshotUrl !== null && (
                    <div className={'flex w-full flex-col gap-y-2'}>
                      <a
                        href={screenshotUrl}
                        target={'_blank'}
                        rel={'noreferrer'}
                        title={'Click to view full screenshot'}
                        className={'relative w-full'}
                      >
                        <img
                          src={screenshotUrl}
                          alt={`${riskIndicator.name} screenshot of the website`}
                          className={'h-auto max-h-60 w-full rounded-2xl object-cover object-top'}
                        />
                      </a>

                      {riskIndicator.sourceUrl && (
                        <a
                          href={riskIndicator.sourceUrl}
                          target={'_blank'}
                          rel={'noreferrer'}
                          className={'mt-2 block max-w-[20rem] truncate'}
                        >
                          {riskIndicator.sourceUrl}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
};
